/**
 * Website Make changes — pure helpers over the page model (no React, no I/O),
 * so they are testable on their own (test-script/test_site_edit_ops.mjs).
 */

import type { Block, PageModel, Section } from '@/pages/designers/page/types'
import type { SiteMenuEntry, SiteMenusSpec } from '@twasta/page-schema'
import type { SiteEditSite } from './api'
import CONTENT from './contentFields.json'
import POLICY from './sitePolicy.json'

export interface ContentField {
  key: string
  label: string
  kind: 'text' | 'long' | 'lines' | 'datetime' | 'button' | 'image' | 'items'
  clears?: string[]
  /** Other keys written with a value (a map's address switches it to addresses). */
  sets?: Record<string, unknown>
  altKey?: string
  noun?: string
  titleKey?: string
  fields?: ContentField[]
  make?: Record<string, unknown>
}

interface BlockSpec { label: string; fields: ContentField[] }

const BLOCKS = (CONTENT as unknown as { blocks: Record<string, BlockSpec> }).blocks
const CONTAINERS = (CONTENT as unknown as { containers: string[] }).containers

export function blockSpec(type: string): BlockSpec | null {
  return BLOCKS[type] || null
}

let seq = 0
export function newId(prefix: string): string {
  seq += 1
  return `${prefix}_${Date.now().toString(36)}${seq.toString(36)}${Math.random().toString(36).slice(2, 5)}`
}

/** A deep copy with every `id` replaced — a copied section must never share a
 *  block id with its original (embed tokens, list bindings and the runtime's
 *  per-block state are keyed by it). */
export function renewIds<T>(value: T): T {
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walk)
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = walk(x)
      if (typeof out.id === 'string') {
        const prefix = (out.id as string).match(/^[a-zA-Z]+/)?.[0] || 'id'
        out.id = newId(prefix)
      }
      return out
    }
    return v
  }
  return walk(value) as T
}

/** Child block lists of a block, from the container paths in contentFields.json. */
function childLists(block: Record<string, unknown>): Block[][] {
  const out: Block[][] = []
  for (const path of CONTAINERS) {
    const [head, star, tail] = path.split('.')
    const v = block[head]
    if (!star) { if (Array.isArray(v)) out.push(v as Block[]); continue }
    if (Array.isArray(v)) {
      for (const item of v) {
        const inner = item && typeof item === 'object' ? (item as Record<string, unknown>)[tail] : null
        if (Array.isArray(inner)) out.push(inner as Block[])
      }
    }
  }
  return out
}

/** Every block of a section, depth first (containers included). */
export function sectionBlocks(sec: Section): Block[] {
  const out: Block[] = []
  const visit = (list: Block[] | undefined) => {
    for (const b of list || []) {
      if (!b || typeof b !== 'object') continue
      out.push(b)
      for (const kids of childLists(b as unknown as Record<string, unknown>)) visit(kids)
    }
  }
  visit(sec.blocks)
  return out
}

/** A copy of `sec` with the block `id` replaced by `next` (at any depth). */
export function replaceBlock(sec: Section, id: string, next: Block): Section {
  const mapList = (list: Block[] | undefined): Block[] | undefined => list?.map((b) => {
    if (!b || typeof b !== 'object') return b
    if (b.id === id) return next
    const copy = { ...(b as unknown as Record<string, unknown>) }
    for (const path of CONTAINERS) {
      const [head, star, tail] = path.split('.')
      const v = copy[head]
      if (!Array.isArray(v)) continue
      copy[head] = star
        ? v.map((item) => (item && typeof item === 'object' && Array.isArray((item as Record<string, unknown>)[tail])
          ? { ...(item as Record<string, unknown>), [tail]: mapList((item as Record<string, unknown>)[tail] as Block[]) }
          : item))
        : mapList(v as Block[])
    }
    return copy as unknown as Block
  })
  return { ...sec, blocks: mapList(sec.blocks) || [] }
}

// ── paths into a block ───────────────────────────────────────────────────

export type Path = (string | number)[]

/** A field key (`title`, `background.imageUrl`) as a path. */
export function keyPath(key: string): Path {
  return key.split('.')
}

export function getAt(obj: unknown, path: Path): unknown {
  let v: unknown = obj
  for (const k of path) {
    if (v == null || typeof v !== 'object') return undefined
    v = (v as Record<string | number, unknown>)[k]
  }
  return v
}

/** A copy of `obj` with `path` set (undefined / '' removes the last key). */
export function setAt<T>(obj: T, path: Path, value: unknown): T {
  if (path.length === 0) return value as T
  const [head, ...rest] = path
  const base = (obj && typeof obj === 'object' ? obj : (typeof head === 'number' ? [] : {})) as Record<string | number, unknown>
  const copy = (Array.isArray(base) ? [...base] : { ...base }) as Record<string | number, unknown>
  if (rest.length === 0) {
    if (value === undefined || value === '') {
      if (Array.isArray(copy)) copy[head as number] = ''
      else delete copy[head]
    } else copy[head] = value
  } else copy[head] = setAt(copy[head], rest, value)
  return copy as T
}

/** Whether a block takes this field from data (`bindings.<key>`): shown,
 *  never typed over — the data would win again on the next render. */
export function fieldBound(block: unknown, key: string): boolean {
  const b = (block as { bindings?: Record<string, unknown> } | null)?.bindings
  if (!b) return false
  // A binding names the field's own key (`card.title`) or the object it is in.
  const parts = key.split('.')
  return parts.some((_, i) => !!b[parts.slice(0, i + 1).join('.')])
}

/** The words of a block that can be typed straight on the page: every text
 *  field contentFields.json lists, at its path, with what typing it clears.
 *  Fields the block takes from data are left out. */
export interface TextTarget { path: Path; value: string; kind: 'text' | 'long'; clears: string[] }

export function textTargets(block: Block): TextTarget[] {
  const spec = blockSpec(block.type)
  const out: TextTarget[] = []
  if (!spec) return out
  const visit = (fields: ContentField[], obj: unknown, prefix: Path, top: boolean) => {
    for (const f of fields) {
      if (top && fieldBound(block, f.key)) continue
      const at = f.key === '@self' ? prefix : [...prefix, ...keyPath(f.key)]
      const v = getAt(obj, f.key === '@self' ? [] : keyPath(f.key))
      if ((f.kind === 'text' || f.kind === 'long') && typeof v === 'string' && v.trim()) {
        out.push({ path: at, value: v, kind: f.kind, clears: f.clears || [] })
      } else if (f.kind === 'button' && v && typeof v === 'object') {
        const label = (v as { label?: unknown }).label
        if (typeof label === 'string' && label.trim()) out.push({ path: [...at, 'label'], value: label, kind: 'text', clears: [] })
      } else if (f.kind === 'lines' && Array.isArray(v)) {
        v.forEach((line, i) => {
          if (typeof line === 'string' && line.trim()) out.push({ path: [...at, i], value: line, kind: 'text', clears: [] })
        })
      } else if (f.kind === 'items' && Array.isArray(v)) {
        v.forEach((item, i) => visit(f.fields || [], item, [...at, i], false))
      }
    }
  }
  visit(spec.fields, block, [], true)
  return out
}

/** Words as a reader sees them (runs of white space are one space). */
export function plainWords(s: string): string {
  return s.replace(/\s+/g, ' ').trim()
}

/** The block after typing `text` at a target: the value, minus what the
 *  renderer would prefer over it (a heading's rich `html`). */
export function typeInto(block: Block, target: TextTarget, text: string): Block {
  let next = setAt(block, target.path, text)
  const parent = target.path.slice(0, -1)
  for (const k of target.clears) next = setAt(next, [...parent, k], undefined)
  return next
}

/** What a block is called for people. */
export function blockLabel(block: Block): string {
  const spec = blockSpec(block.type)
  if (spec) return spec.label
  const t = String(block.type || 'part').replace(/_/g, ' ')
  return t.charAt(0).toUpperCase() + t.slice(1)
}

// ── blocks inside a page ─────────────────────────────────────────────────

/** Apply `fn` to the ONE block list (a section's, or a container's) that
 *  holds `id`; every other list is returned as it was. */
function updateListHolding(page: PageModel, id: string, fn: (list: Block[], index: number) => Block[]): PageModel {
  let done = false
  const mapList = (list: Block[] | undefined): Block[] | undefined => {
    if (!Array.isArray(list) || done) return list
    const i = list.findIndex((b) => b && b.id === id)
    if (i >= 0) { done = true; return fn(list, i) }
    let changed = false
    const out = list.map((b) => {
      if (done || !b || typeof b !== 'object') return b
      const copy = { ...(b as unknown as Record<string, unknown>) }
      let touched = false
      for (const path of CONTAINERS) {
        if (done) break
        const [head, star, tail] = path.split('.')
        const v = copy[head]
        if (!Array.isArray(v)) continue
        if (!star) {
          const next = mapList(v as Block[])
          if (next !== v) { copy[head] = next; touched = true }
        } else {
          const items = v.map((item) => {
            if (done || !item || typeof item !== 'object') return item
            const inner = (item as Record<string, unknown>)[tail]
            if (!Array.isArray(inner)) return item
            const next = mapList(inner as Block[])
            return next === inner ? item : { ...(item as Record<string, unknown>), [tail]: next }
          })
          if (items.some((x, k) => x !== v[k])) { copy[head] = items; touched = true }
        }
      }
      if (!touched) return b
      changed = true
      return copy as unknown as Block
    })
    return changed ? out : list
  }
  const sections = (page.sections || []).map((s) => {
    if (done) return s
    const blocks = mapList(s.blocks)
    return blocks === s.blocks ? s : { ...s, blocks: blocks || [] }
  })
  return done ? { ...page, sections } : page
}

/** The stored block with this id, anywhere on the page. */
export function findBlock(page: PageModel, id: string): Block | null {
  for (const s of page.sections || []) {
    const hit = sectionBlocks(s).find((b) => b.id === id)
    if (hit) return hit
  }
  return null
}

/** Every block of the page by id: the block, where it sits in its list
 *  (index / how many share the list) and its section. One walk per page. */
export interface BlockSpot { block: Block; index: number; count: number; sectionId: string }

export function blockIndex(page: PageModel): Map<string, BlockSpot> {
  const out = new Map<string, BlockSpot>()
  const visit = (list: Block[] | undefined, sectionId: string) => {
    if (!Array.isArray(list)) return
    list.forEach((b, index) => {
      if (!b || typeof b !== 'object') return
      if (typeof b.id === 'string' && !out.has(b.id)) out.set(b.id, { block: b, index, count: list.length, sectionId })
      for (const kids of childLists(b as unknown as Record<string, unknown>)) visit(kids, sectionId)
    })
  }
  for (const s of page.sections || []) visit(s.blocks, s.id)
  return out
}

export function replaceBlockIn(page: PageModel, id: string, next: Block): PageModel {
  return updateListHolding(page, id, (list, i) => list.map((b, k) => (k === i ? next : b)))
}

export function moveBlock(page: PageModel, id: string, dir: -1 | 1): PageModel {
  return updateListHolding(page, id, (list, i) => {
    const j = i + dir
    if (j < 0 || j >= list.length) return list
    const out = [...list]
    const [x] = out.splice(i, 1)
    out.splice(j, 0, x)
    return out
  })
}

export function removeBlock(page: PageModel, id: string): PageModel {
  return updateListHolding(page, id, (list, i) => list.filter((_, k) => k !== i))
}

/** A new block right after `afterId`, in the same list. */
export function insertBlockAfter(page: PageModel, afterId: string, block: Block): PageModel {
  return updateListHolding(page, afterId, (list, i) => [...list.slice(0, i + 1), block, ...list.slice(i + 1)])
}

/** A new block at the end of a section. */
export function appendBlock(page: PageModel, sectionId: string, block: Block): PageModel {
  return { ...page, sections: (page.sections || []).map((s) => (s.id === sectionId ? { ...s, blocks: [...(s.blocks || []), block] } : s)) }
}

export function copyBlock(page: PageModel, id: string): { page: PageModel; copy: Block | null } {
  const original = findBlock(page, id)
  if (!original) return { page, copy: null }
  const copy = renewIds(original)
  return { page: insertBlockAfter(page, id, copy), copy }
}

/** The section's name for people: its own name, else what its first block is. */
export function sectionLabel(sec: Section): string {
  if (sec.name && sec.name.trim()) return sec.name.trim()
  for (const b of sectionBlocks(sec)) {
    const spec = blockSpec(b.type)
    if (spec) return spec.label
  }
  return 'Section'
}

/** A page built from a flat block list (no sections) as ONE "Main" section —
 *  what the page designer does on load (designers/page/migrate.ts `toV2`), so
 *  the mode can put its controls on it and add sections without dropping the
 *  page's content. Stored pages keep an empty legacy list, like the designer's. */
export function withSections(page: PageModel): PageModel {
  if ((page.sections || []).length > 0 || !Array.isArray(page.blocks) || page.blocks.length === 0) return page
  const main: Section = { id: newId('sec'), name: 'Main', tag: 'main', blocks: page.blocks }
  return { ...page, sections: [main], blocks: [] }
}

/** Whether a page departs from its website's style (its own theme, or
 *  one-page `local` colours) — page_themes' effective_ref read the other way. */
export function ownLook(page: PageModel, siteRef: string | undefined): boolean {
  const t = (page.theme || {}) as { ref?: string; local?: Record<string, unknown>; inherited?: boolean }
  if (!siteRef) return false
  if (t.local && Object.keys(t.local).length > 0) return true
  return !t.inherited && !!t.ref && t.ref !== siteRef
}

/** The page following its website's style again; the server materializes it. */
export function followSiteLook(page: PageModel, siteRef: string): PageModel {
  return { ...page, theme: { ref: siteRef, inherited: true } as unknown as PageModel['theme'] }
}

export function moveSection(page: PageModel, index: number, dir: -1 | 1): PageModel {
  const list = [...(page.sections || [])]
  const j = index + dir
  if (j < 0 || j >= list.length) return page
  const [x] = list.splice(index, 1)
  list.splice(j, 0, x)
  return { ...page, sections: list }
}

export function removeSection(page: PageModel, id: string): PageModel {
  return { ...page, sections: (page.sections || []).filter((s) => s.id !== id) }
}

export function insertSection(page: PageModel, at: number, sec: Section): PageModel {
  const list = [...(page.sections || [])]
  list.splice(Math.max(0, Math.min(at, list.length)), 0, sec)
  return { ...page, sections: list }
}

export function replaceSection(page: PageModel, sec: Section): PageModel {
  return { ...page, sections: (page.sections || []).map((s) => (s.id === sec.id ? sec : s)) }
}

export function copySection(page: PageModel, id: string): { page: PageModel; copy: Section | null } {
  const list = page.sections || []
  const i = list.findIndex((s) => s.id === id)
  if (i < 0) return { page, copy: null }
  const copy = renewIds(list[i])
  if (copy.name) copy.name = `${copy.name} (copy)`
  return { page: insertSection(page, i + 1, copy), copy }
}

/**
 * The page to DRAW while editing: the stored model, carrying whatever the
 * server added to the served copy of the same blocks (page-bound embed tokens,
 * baked data) so data-bound blocks keep working in the mode. The stored model
 * decides structure and every value it has; the served one only fills keys the
 * stored one does not carry.
 */
export function overlayServed(served: PageModel | null, stored: PageModel): PageModel {
  if (!served) return stored
  const byId = new Map<string, Record<string, unknown>>()
  const index = (v: unknown) => {
    if (Array.isArray(v)) { v.forEach(index); return }
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>
      if (typeof o.id === 'string' && typeof o.type === 'string') byId.set(o.id, o)
      Object.values(o).forEach(index)
    }
  }
  index(served.sections)
  const merge = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(merge)
    if (v && typeof v === 'object') {
      const o: Record<string, unknown> = {}
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) o[k] = merge(x)
      const twin = typeof o.id === 'string' && typeof o.type === 'string' ? byId.get(o.id) : undefined
      if (twin) for (const [k, x] of Object.entries(twin)) if (!(k in o)) o[k] = x
      return o
    }
    return v
  }
  const out = { ...served, ...stored, sections: merge(stored.sections) as Section[] }
  return out
}

// ── look presets (sitePolicy.json) ───────────────────────────────────────

type Spacing = { id: string; label: string; padding: { top: number; bottom: number } | null }
type Devices = { id: string; label: string; devices: string[] | null }

export const SPACING = POLICY.spacing as Spacing[]
export const DEVICES = POLICY.devices as Devices[]
export const WIDTHS = POLICY.widths as { id: 'contained' | 'full'; label: string }[]
export const SWATCHES = POLICY.swatches as string[]
export const FONTS = POLICY.fonts as { id: string; label: string; body: string; heading: string }[]
export const PAGE_STARTS = POLICY.pageStarts as { id: string; label: string }[]
export const HEADER_SCROLL = POLICY.headerScroll as { id: 'static' | 'sticky' | 'auto-hide'; label: string }[]
export const PLAIN = POLICY.plainBackground as { id: string; label: string }
export const AI_IDEAS = POLICY.askAi.ideas as string[]
export const AI_PLACEHOLDER = POLICY.askAi.placeholder as string
/** What "+ Add a block" offers: a block type and the words it starts with
 *  (laid over the page designer's own new block, makeBlock). */
export const ADD_BLOCKS = POLICY.addBlocks as { type: string; label: string; description: string; set: Record<string, unknown> }[]

export function spacingOf(sec: Section): string {
  const pad = (sec.style as { padding?: { top?: number } } | undefined)?.padding
  if (!pad || pad.top == null) return 'normal'
  const hit = SPACING.find((s) => s.padding && s.padding.top === pad.top)
  return hit ? hit.id : 'custom'
}

export function withSpacing(sec: Section, id: string): Section {
  const preset = SPACING.find((s) => s.id === id)
  if (!preset) return sec
  const style = { ...(sec.style || {}) } as Record<string, unknown>
  if (preset.padding) style.padding = { ...((style.padding as object) || {}), ...preset.padding }
  else delete style.padding
  return { ...sec, style: style as Section['style'] }
}

export function devicesOf(sec: Section): string {
  const d = sec.visibility?.devices
  if (!d || d.length === 0) return 'all'
  const key = [...d].sort().join(',')
  const hit = DEVICES.find((x) => x.devices && [...x.devices].sort().join(',') === key)
  return hit ? hit.id : 'custom'
}

export function withDevices(sec: Section, id: string): Section {
  const preset = DEVICES.find((x) => x.id === id)
  if (!preset) return sec
  const vis = { ...(sec.visibility || {}) }
  if (preset.devices) vis.devices = preset.devices as typeof vis.devices
  else delete vis.devices
  return { ...sec, visibility: vis }
}

/** Whether a section is shown on the viewport being previewed. */
export function shownOn(sec: Section, viewport: 'desktop' | 'mobile'): boolean {
  const d = sec.visibility?.devices
  return !d || d.length === 0 || d.includes(viewport)
}

/** WCAG contrast ratio of two #rrggbb colours. */
export function contrast(a: string, b: string): number {
  const lum = (hex: string) => {
    const h = hex.replace('#', '')
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
    const n = parseInt(full.slice(0, 6), 16)
    if (Number.isNaN(n)) return 0
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
      const s = v / 255
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
    })
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2]
  }
  const x = lum(a), y = lum(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

// ── site menus ───────────────────────────────────────────────────────────

/** The header menu after turning one page on or off. With no named header
 *  menu the site lists every page, so the first change names that list. */
export function toggleMenuPage(site: Pick<SiteEditSite, 'settings' | 'pages'>, obj: string, on: boolean): SiteMenusSpec {
  const menus: SiteMenusSpec = { ...(site.settings.menus || {}) }
  const strip = (entries: SiteMenuEntry[]): SiteMenuEntry[] => entries
    .filter((e) => (e as { page?: string }).page !== obj)
    .map((e) => (Array.isArray((e as { children?: SiteMenuEntry[] }).children)
      ? { ...e, children: strip((e as { children: SiteMenuEntry[] }).children) } as SiteMenuEntry
      : e))
  const current: SiteMenuEntry[] = menus.header
    ? [...menus.header]
    : site.pages.filter((p) => !p.chrome && p.in_menu).map((p) => ({ page: p.obj_name }) as SiteMenuEntry)
  menus.header = on ? [...strip(current), { page: obj } as SiteMenuEntry] : strip(current)
  return menus
}

